/**
 * Outbound — reprint sealed carton ZPL labels (same print relay path as Pre-Pack).
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2, Printer } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

import {
  fetchPrePackTransfer,
  printCartonLabel,
  printPrePackTransferCartonLabel,
} from "@/pages/prepack/prepackApi";

import type { OutboundQueueEntry, SkuQuantity } from "./shippingHubTypes";
import { OUTBOUND_ZPL_DOCUMENTS } from "./outboundPrintDocuments";

export type OutboundReprintCartonLabelTarget =
  | { kind: "shipment"; shipmentId: number; cartonNumbers: number[] }
  | { kind: "transfer"; transferId: number; transferNumber: string };

function distinctPositiveCartonKeys(items: SkuQuantity[] | null): number[] {
  if (items == null || items.length === 0) return [];
  const keys = new Set<number>();
  for (const item of items) {
    const key = item.carton_key ?? 0;
    if (key > 0) keys.add(key);
  }
  return [...keys].sort((a, b) => a - b);
}

export function outboundReprintTargetFromEntry(
  entry: OutboundQueueEntry,
): OutboundReprintCartonLabelTarget | null {
  if (entry.kind === "shipment") {
    const cartonNumbers = distinctPositiveCartonKeys(entry.shipment.sku_quantities);
    if (cartonNumbers.length === 0 && entry.shipment.carton_count <= 0) return null;
    return {
      kind: "shipment",
      shipmentId: entry.shipment.shipment_id,
      cartonNumbers:
        cartonNumbers.length > 0
          ? cartonNumbers
          : Array.from({ length: entry.shipment.carton_count }, (_, index) => index + 1),
    };
  }
  if (entry.transfer.box_count <= 0) return null;
  return {
    kind: "transfer",
    transferId: entry.transfer.id,
    transferNumber: entry.transfer.transfer_number,
  };
}

function titleForTarget(target: OutboundReprintCartonLabelTarget): string {
  if (target.kind === "shipment") {
    return `Reprint carton labels — Shipment ${String(target.shipmentId)}`;
  }
  return `Reprint carton labels — ${target.transferNumber}`;
}

export function OutboundReprintCartonLabelModal({
  target,
  onClose,
  station,
  onStationChange,
  selectedPrinter,
  unavailableMessage,
}: {
  target: OutboundReprintCartonLabelTarget;
  onClose: () => void;
  station: string;
  onStationChange: (station: string) => void;
  selectedPrinter: string;
  unavailableMessage: string | null;
}): JSX.Element {

  const transferBoxesQuery = useQuery({
    queryKey: ["hub", "outbound", "reprint-transfer-boxes", target.kind === "transfer" ? target.transferId : 0],
    queryFn: () => {
      if (target.kind !== "transfer") {
        throw new Error("Transfer carton list is only loaded for transfer reprints.");
      }
      return fetchPrePackTransfer(target.transferId);
    },
    enabled: target.kind === "transfer",
    staleTime: 15_000,
  });

  const cartonOptions = useMemo(() => {
    if (target.kind === "shipment") {
      return target.cartonNumbers.map((cartonNumber) => ({
        key: String(cartonNumber),
        cartonNumber,
        label: `Carton ${String(cartonNumber)}`,
        printable: true,
        hint: null as string | null,
      }));
    }
    const boxes = transferBoxesQuery.data?.boxes ?? [];
    return [...boxes]
      .sort((left, right) => left.box_number - right.box_number)
      .map((box) => ({
        key: String(box.id),
        cartonNumber: box.box_number,
        label: `Carton #${String(box.box_number)}`,
        printable: box.sealed,
        hint: box.sealed
          ? box.label_printed_at != null
            ? "Printed previously"
            : "Sealed · not printed yet"
          : "Seal in Pre-Pack before printing",
      }));
  }, [target, transferBoxesQuery.data?.boxes]);

  const printableKeys = useMemo(
    () => cartonOptions.filter((row) => row.printable).map((row) => row.key),
    [cartonOptions],
  );

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [printing, setPrinting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setSelectedKeys(new Set(printableKeys));
    setStatusMessage(null);
    setErrorMessage(null);
  }, [printableKeys]);

  const selectedPrintableCount = useMemo(() => {
    let count = 0;
    for (const row of cartonOptions) {
      if (row.printable && selectedKeys.has(row.key)) count += 1;
    }
    return count;
  }, [cartonOptions, selectedKeys]);

  function toggleKey(key: string, printable: boolean): void {
    if (!printable || printing) return;
    setSelectedKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function selectAllPrintable(): void {
    setSelectedKeys(new Set(printableKeys));
  }

  function clearSelection(): void {
    setSelectedKeys(new Set());
  }

  async function handlePrint(): Promise<void> {
    const printer = selectedPrinter.trim();
    if (printer === "") {
      setErrorMessage("Select a label printer first.");
      return;
    }
    const toPrint = cartonOptions.filter((row) => row.printable && selectedKeys.has(row.key));
    if (toPrint.length === 0) {
      setErrorMessage("Select at least one sealed carton to reprint.");
      return;
    }

    setPrinting(true);
    setErrorMessage(null);
    setStatusMessage(null);
    let printedCount = 0;
    let labelJobs = 0;
    try {
      for (const row of toPrint) {
        setStatusMessage(`Printing ${row.label}…`);
        if (target.kind === "shipment") {
          const result = await printCartonLabel(target.shipmentId, row.cartonNumber, printer);
          printedCount += 1;
          labelJobs += result.labelCount ?? 1;
        } else {
          const result = await printPrePackTransferCartonLabel(
            target.transferId,
            row.cartonNumber,
            printer,
          );
          printedCount += 1;
          labelJobs += result.labelCount ?? 1;
        }
      }
      const cartonWord = printedCount === 1 ? "carton" : "cartons";
      const labelWord = labelJobs === 1 ? "label" : "labels";
      setStatusMessage(
        labelJobs === printedCount
          ? `Sent ${String(printedCount)} ${cartonWord} to the printer.`
          : `Sent ${String(printedCount)} ${cartonWord} (${String(labelJobs)} ${labelWord}) to the printer.`,
      );
    } catch (error) {
      setErrorMessage(error instanceof ApiError ? error.message : String(error));
      if (printedCount > 0) {
        setStatusMessage(
          `Printed ${String(printedCount)} carton${printedCount === 1 ? "" : "s"} before the error.`,
        );
      }
    } finally {
      setPrinting(false);
    }
  }

  const listLoading = target.kind === "transfer" && transferBoxesQuery.isLoading;
  const listError =
    target.kind === "transfer" && transferBoxesQuery.isError
      ? transferBoxesQuery.error instanceof ApiError
        ? transferBoxesQuery.error.message
        : "Could not load transfer cartons."
      : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(event) => {
        if (event.target === event.currentTarget && !printing) onClose();
      }}
    >
      <div className="flex max-h-[min(90vh,640px)] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl">
        <div className="border-b border-slate-100 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">{titleForTarget(target)}</h2>
          <p className="mt-1 text-sm text-slate-500">
            Select sealed carton(s) and send the Pre-Pack carton label to the ZPL printer again.
          </p>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
          <ZplLocationSelect
            id="outbound-reprint-carton-location"
            value={station}
            onChange={onStationChange}
            unavailableMessage={unavailableMessage}
            disabled={printing}
            documents={OUTBOUND_ZPL_DOCUMENTS}
          />

          {listLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading cartons…
            </div>
          ) : listError != null ? (
            <p className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {listError}
            </p>
          ) : cartonOptions.length === 0 ? (
            <p className="py-4 text-sm text-slate-500">No cartons found for this outbound line.</p>
          ) : (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Cartons
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="text-xs font-medium text-brand-700 hover:underline disabled:text-slate-400"
                    disabled={printing || printableKeys.length === 0}
                    onClick={selectAllPrintable}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="text-xs font-medium text-slate-600 hover:underline disabled:text-slate-400"
                    disabled={printing || selectedKeys.size === 0}
                    onClick={clearSelection}
                  >
                    Clear
                  </button>
                </div>
              </div>
              <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
                {cartonOptions.map((row) => {
                  const checked = selectedKeys.has(row.key);
                  return (
                    <li key={row.key}>
                      <label
                        className={cn(
                          "flex cursor-pointer items-start gap-3 px-3 py-2.5",
                          !row.printable && "cursor-not-allowed bg-slate-50 opacity-70",
                          row.printable && checked && "bg-brand-50/40",
                        )}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                          checked={checked}
                          disabled={!row.printable || printing}
                          onChange={() => toggleKey(row.key, row.printable)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-slate-900">{row.label}</span>
                          {row.hint != null ? (
                            <span className="block text-xs text-slate-500">{row.hint}</span>
                          ) : null}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {errorMessage != null ? (
            <p className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {errorMessage}
            </p>
          ) : null}
          {statusMessage != null ? (
            <p className="text-sm text-emerald-700">{statusMessage}</p>
          ) : null}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <Button type="button" variant="secondary" disabled={printing} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={printing || listLoading || listError != null || selectedPrintableCount === 0}
            onClick={() => void handlePrint()}
          >
            {printing ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Printer className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            )}
            {printing
              ? "Printing…"
              : selectedPrintableCount <= 1
                ? "Reprint selected"
                : `Reprint ${String(selectedPrintableCount)} cartons`}
          </Button>
        </div>
      </div>
    </div>
  );
}
